import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle, Printer } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking, paid, failed
  const [listingId, setListingId] = useState(null);
  const [kind, setKind] = useState("cartaz");
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      if (attempts.current >= 8) { setStatus("failed"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setListingId(data.listing_id);
        if (data.kind) setKind(data.kind);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setStatus("failed"); return; }
      } catch { /* retry */ }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const isDestaque = kind === "destaque";

  return (
    <div className="min-h-screen flex items-center justify-center px-6" data-testid="payment-success-page">
      <div className="w-full max-w-md bg-white border border-border rounded-sm p-10 text-center">
        {status === "checking" && (<>
          <Loader2 className="w-12 h-12 text-blaze animate-spin mx-auto" />
          <h1 className="mt-5 font-heading font-800 text-2xl">Confirmando pagamento...</h1>
          <p className="mt-2 text-muted-foreground">Aguarde um instante.</p>
        </>)}
        {status === "paid" && (<>
          <div className="w-16 h-16 rounded-sm bg-green-100 flex items-center justify-center mx-auto"><CheckCircle2 className="w-9 h-9 text-green-600" /></div>
          <h1 className="mt-5 font-heading font-800 text-2xl">Pagamento confirmado!</h1>
          {isDestaque ? (
            <>
              <p className="mt-2 text-muted-foreground">Sua assinatura de destaque está ativa e seu anúncio já está <b>no topo da vitrine</b>. Renova sozinha até você cancelar no painel. 🚀</p>
              <Button className="mt-6 w-full h-12 bg-blaze hover:bg-obsidian text-white font-700 rounded-sm" onClick={() => navigate("/painel")} data-testid="go-painel-btn">
                Ver meu painel
              </Button>
            </>
          ) : (
            <>
              <p className="mt-2 text-muted-foreground">Sua assinatura do Kit de Divulgação com QR (trimestral) está ativa. Seus materiais — cartaz A4, cartão postal e cartão de visita — estão prontos para imprimir. Renova sozinha até você cancelar no painel.</p>
              <Button className="mt-6 w-full h-12 bg-blaze hover:bg-obsidian text-white font-700 rounded-sm" onClick={() => navigate(`/cartaz/${listingId}`)} data-testid="go-cartaz-btn">
                <Printer className="w-4 h-4 mr-2" /> Ver e imprimir materiais
              </Button>
              <Button variant="ghost" className="mt-2 w-full" onClick={() => navigate("/painel")}>Voltar ao painel</Button>
            </>
          )}
        </>)}
        {status === "failed" && (<>
          <div className="w-16 h-16 rounded-sm bg-blaze/10 flex items-center justify-center mx-auto"><XCircle className="w-9 h-9 text-blaze" /></div>
          <h1 className="mt-5 font-heading font-800 text-2xl">Não foi possível confirmar</h1>
          <p className="mt-2 text-muted-foreground">Se o valor foi cobrado, seus materiais serão liberados em instantes. Verifique no painel.</p>
          <Button className="mt-6 w-full bg-obsidian hover:bg-blaze text-white rounded-sm" onClick={() => navigate("/painel")}>Voltar ao painel</Button>
        </>)}
      </div>
    </div>
  );
}
